import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
interface Project {
  id: string;
  name: string;
  description: string;
  status: 'In Progress' | 'Launched' | 'Beta';
  imageUrl: string;
  url?: string;
}
const projects: Project[] = [{
  id: '1',
  name: 'Kaamly',
  description: 'Automated Immigration Portal appointment scheduling. Monitor availability signals and book the earliest slots automatically and securely.',
  status: 'Launched',
  imageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/1zNE4fiETFqmZHBGhLMzr4/kaamly.io_source.png',
  url: 'https://kaamly.io'
}, {
  id: '2',
  name: 'useGigi',
  description: 'AI-Powered Outreach, Human-Guided by Expert SDRs. Personalized B2B campaigns managed by sales experts with 17+ years of experience.',
  status: 'Launched',
  imageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/29dKSCubHT5etdNC9EsBR2/usegigi.ai.png',
  url: 'https://usegigi.ai'
}, {
  id: '3',
  name: 'AIpply',
  description: 'AI-Powered Job Search Revolution. Apply to hundreds of jobs with one click. Let AI personalize your applications and boost your chances of landing interviews.',
  status: 'Beta',
  imageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/khsVD6e2HPjkmHzzv2SQyU/aipply.png',
  url: 'https://aipply.ai'
}, {
  id: '4',
  name: 'LeanOps',
  description: "Global Talent, Local Culture. Connecting US businesses with senior tech specialists from Romania's top-tier companies for scalable solutions.",
  status: 'Launched',
  imageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/hXsEaCcQ4J3pB3bWNSYDrA/image.png',
  url: 'https://leanops.co'
}, {
  id: '5',
  name: 'DevStaff.ai',
  description: "Elite AI Talent. On Demand. Eastern Europe's first AI talent platform. Get fully vetted AI engineers in under 2 weeks with 100% pre-vetted talent.",
  status: 'In Progress',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/7KnQ5RSRvbPEkAfhvjmSP3/image.png',
  url: 'https://devstaff.ai'
}, {
  id: '6',
  name: 'PromptHire',
  description: 'End-to-end staffing workflow automation. AI-powered platform that streamlines hiring from brief to invoice with instant demand capture, AI matching, and one-click e-signatures.',
  status: 'In Progress',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/cQt1WR8fZJeHXysCTjsfTk/image.png',
  url: 'https://prompthire.io'
}, {
  id: '7',
  name: 'EndorseHive',
  description: 'Turn Your LinkedIn Into a Revenue Stream. Get paid to endorse brands you believe in, grow your professional network, and maintain authentic connections.',
  status: 'In Progress',
  imageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/mVL8P5zJQjs4hd9KC63eQk/image.png',
  url: 'https://endorsehive.com'
}, {
  id: '8',
  name: 'EliteFlowX',
  description: 'Hiring Simplified. Your next great hire is just a chat away. AI-powered assistant matches you with pre-vetted talent in minutes, guided by a dedicated human partner.',
  status: 'Launched',
  imageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/7fY1VvGwFJefiGPvG5q198/image.png',
  url: 'https://eliteflowx.com'
}];
export function ProjectCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const nextSlide = () => {
    setCurrentIndex(prev => (prev + 1) % projects.length);
  };
  const prevSlide = () => {
    setCurrentIndex(prev => (prev - 1 + projects.length) % projects.length);
  };
  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };
  // Auto-play carousel
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      nextSlide();
    }, 5000); // Change slide every 5 seconds
    return () => clearInterval(interval);
  }, [currentIndex, isPaused]);
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        prevSlide();
        setIsPaused(true); // Pause auto-play on manual interaction
      } else if (e.key === 'ArrowRight') {
        nextSlide();
        setIsPaused(true); // Pause auto-play on manual interaction
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  const currentProject = projects[currentIndex];
  const statusColors = {
    'In Progress': 'bg-blue-500',
    Launched: 'bg-green-500',
    Beta: 'bg-yellow-500'
  };
  const handleManualNavigation = (action: () => void) => {
    action();
    setIsPaused(true); // Pause auto-play when user manually navigates
  };
  return <section className="py-12 md:py-20 bg-white dark:bg-gray-900" data-id="element-271">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-272">
        <div className="text-center mb-8 md:mb-16" data-id="element-273">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-3 md:mb-4" data-id="element-274">
            Live Projects
          </h2>
          <p className="text-base md:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto px-4" data-id="element-275">
            Track our ongoing success stories and see how we're helping
            innovators bring their ideas to life.
          </p>
        </div>

        <div className="relative max-w-6xl mx-auto" data-id="element-276">
          <div className="flex items-center justify-center gap-2 md:gap-8" data-id="element-277">
            {/* Previous Button */}
            <button onClick={() => handleManualNavigation(prevSlide)} className="flex-shrink-0 w-10 h-10 md:w-14 md:h-14 rounded-full bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 flex items-center justify-center hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-300 hover:scale-110 shadow-lg z-10" aria-label="Previous project" data-id="element-278">
              <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-gray-600 dark:text-gray-300" data-id="element-279" />
            </button>

            {/* Carousel Card */}
            <div className="relative w-full max-w-5xl" onMouseEnter={() => {
            setIsHovered(true);
            setIsPaused(true);
          }} onMouseLeave={() => {
            setIsHovered(false);
            setIsPaused(false);
          }} onTouchStart={() => setIsPaused(true)} data-id="element-280">
              <div className="relative bg-white dark:bg-gray-800 rounded-2xl md:rounded-3xl shadow-2xl overflow-hidden transition-all duration-500" data-id="element-281">
                {/* Project Screenshot */}
                <div className="relative h-[300px] sm:h-[400px] md:h-[600px] overflow-hidden flex items-center justify-center bg-gray-50 dark:bg-gray-900" data-id="element-282">
                  <img src={currentProject.imageUrl} alt={currentProject.name} className={`w-full h-full object-cover ${currentProject.id === '5' ? 'object-center' : 'object-top'} transition-all duration-700 ${isHovered ? 'scale-105 blur-sm' : 'scale-100'}`} data-id="element-283" />

                  {/* Gradient Overlay on Hover */}
                  <div className={`absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent transition-opacity duration-500 ${isHovered ? 'opacity-100' : 'opacity-0'}`} data-id="element-284" />

                  {/* Project Info Overlay */}
                  <div className={`absolute inset-0 flex flex-col items-center justify-end p-6 md:p-12 transition-all duration-500 ${isHovered ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} data-id="element-285">
                    <div className="text-center space-y-2 md:space-y-4 max-w-2xl" data-id="element-286">
                      <h3 className="text-2xl md:text-4xl font-bold text-white" data-id="element-287">
                        {currentProject.name}
                      </h3>
                      <p className="text-sm md:text-xl text-gray-200 line-clamp-3 md:line-clamp-none" data-id="element-288">
                        {currentProject.description}
                      </p>
                      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 pt-2 md:pt-4" data-id="element-289">
                        <span className={`px-4 md:px-6 py-1.5 md:py-2 rounded-full text-white text-sm md:text-base font-semibold ${statusColors[currentProject.status]}`} data-id="element-290">
                          {currentProject.status}
                        </span>
                        {currentProject.url && <a href={currentProject.url} target="_blank" rel="noopener noreferrer" className="px-4 md:px-6 py-1.5 md:py-2 bg-white text-gray-900 rounded-full text-sm md:text-base font-semibold hover:bg-gray-100 transition-colors" data-id="element-291">
                            Visit Project →
                          </a>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Carousel Indicators */}
              <div className="flex justify-center gap-2 md:gap-3 mt-6 md:mt-8" data-id="element-292">
                {projects.map((_, index) => <button key={index} onClick={() => handleManualNavigation(() => goToSlide(index))} className={`transition-all duration-300 rounded-full ${index === currentIndex ? 'w-8 md:w-12 h-2 md:h-3 bg-blue-600 dark:bg-blue-400' : 'w-2 md:w-3 h-2 md:h-3 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'}`} aria-label={`Go to project ${index + 1}`} data-id="element-293" />)}
              </div>
            </div>

            {/* Next Button */}
            <button onClick={() => handleManualNavigation(nextSlide)} className="flex-shrink-0 w-10 h-10 md:w-14 md:h-14 rounded-full bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 flex items-center justify-center hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-300 hover:scale-110 shadow-lg z-10" aria-label="Next project" data-id="element-294">
              <ChevronRight className="w-5 h-5 md:w-6 md:h-6 text-gray-600 dark:text-gray-300" data-id="element-295" />
            </button>
          </div>
        </div>
      </div>
    </section>;
}